import { imageRefToDataUrl } from './imageRefToDataUrl';

const STORAGE_KEY = 'nyafaz_look_session_v1';
const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.9;

export const LOOK_MODULE_IDS = Object.freeze({
  MAKEUP: 'makeup',
  HAIRSTYLE: 'hairstyle',
  HAIRCOLOR: 'haircolor',
});

function read() {
  try {
    let raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      try {
        raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          sessionStorage.setItem(STORAGE_KEY, raw);
        }
      } catch {
        /* ignore */
      }
    }
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function write(session) {
  const json = JSON.stringify(session);
  try {
    sessionStorage.setItem(STORAGE_KEY, json);
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(STORAGE_KEY, json);
    return true;
  } catch {
    return false;
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode image'));
    img.src = src;
  });
}

/** Any image ref (data:, blob:, https:) → JPEG data URL small enough for sessionStorage. */
export async function toPortableImage(imageRef) {
  const dataUrl = await imageRefToDataUrl(imageRef);
  try {
    const img = await loadImage(dataUrl);
    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  } catch {
    return dataUrl;
  }
}

export function startLookSession(original) {
  if (!original) return;
  const prev = read();
  if (prev?.original === original) return;
  write({ original, current: original, applied: [] });
}

/** Fresh upload / capture in a studio — resets the shared session original. */
export function beginFreshLookCapture(image) {
  if (!image) return;
  write({ original: image, current: image, applied: [] });
}

export function isHairColorHandoffFromLook(state) {
  const from = state?.fromLookModule;
  return from === LOOK_MODULE_IDS.MAKEUP || from === LOOK_MODULE_IDS.HAIRSTYLE;
}

/** Hairstyle try-on should keep makeup / prior studio work from the handed-off photo. */
export function shouldApplyHairstyleOnCurrentLook(state) {
  const from = state?.fromLookModule;
  return from === LOOK_MODULE_IDS.MAKEUP || from === LOOK_MODULE_IDS.HAIRCOLOR;
}

/**
 * Makeup studio: generate from session original. Hairstyle: bare capture unless
 * handoff from makeup/hair color — then apply on the shared current look.
 */
export function resolveMakeupStyleStudioEntry(state) {
  const capture = state?.image ? String(state.image) : '';
  const from = state?.fromLookModule;
  if (capture && !from) {
    startLookSession(capture);
    return { displayImage: capture, applyOriginal: capture };
  }
  if (capture && from) {
    const original = getLookOriginal() || capture;
    if (!getLookOriginal()) startLookSession(capture);
    return { displayImage: capture, applyOriginal: original };
  }
  const original = getLookOriginal();
  const current = getCurrentLook();
  const display = current || original;
  return { displayImage: display, applyOriginal: original || display };
}

/**
 * Hair color studio: direct entry applies on the session original; handoff from
 * makeup or hairstyle applies on the shared current look (not the bare original).
 */
export function resolveHairColorStudioEntry(state) {
  const capture = state?.image ? String(state.image) : '';
  const from = state?.fromLookModule;
  if (capture && !from) {
    startLookSession(capture);
    return { displayImage: capture, applyBase: capture, handoffBase: '' };
  }
  if (capture && isHairColorHandoffFromLook(state)) {
    const original = getLookOriginal() || '';
    if (!original) startLookSession(capture);
    return { displayImage: capture, applyBase: capture, handoffBase: capture };
  }
  if (capture && from) {
    const original = getLookOriginal() || capture;
    return { displayImage: capture, applyBase: original, handoffBase: '' };
  }
  const original = getLookOriginal();
  const current = getCurrentLook();
  const display = current || original;
  return {
    displayImage: display,
    applyBase: original || display,
    handoffBase: '',
  };
}

export function getLookOriginal() {
  return read()?.original || '';
}

export function getCurrentLook() {
  return read()?.current || '';
}

export function getAppliedLookModules() {
  const applied = read()?.applied;
  return Array.isArray(applied) ? applied : [];
}

export function clearLookSession() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Store a studio result as the look the next studio continues from. */
export async function saveLookResult(moduleId, imageRef) {
  if (!imageRef) return '';
  const image = await toPortableImage(imageRef);
  const prev = read() || { original: image, applied: [] };
  const applied = Array.isArray(prev.applied) ? prev.applied.filter((m) => m !== moduleId) : [];
  write({ ...prev, current: image, applied: moduleId ? [...applied, moduleId] : applied });
  return image;
}

/**
 * Studio entry image: a fresh capture starts a new session; a handoff from another
 * studio (state.fromLookModule) continues it; a reload falls back to the saved look.
 */
export function resolveStudioEntryImage(state) {
  return resolveMakeupStyleStudioEntry(state).displayImage;
}
